import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { cqPathQuery, runTraceroute, type CqTraceDetail, type CqTraceItem, type CqTraceReason } from '../api/connectionQuality'
import { Spinner } from '../ui/Spinner'
import { CqPathGraph } from './CqPathGraph'
import './CqPath.css'

/**
 * Sectiunea "Path" a ecranului Connection Quality: traseul spre tinta.
 *
 * Selectorul arata ultimele trasee, cu motivul lor (manual = butonul de aici,
 * periodic = agentul singur, anomaly = agentul cand a vazut o problema) si
 * semnul "path changed" cand drumul e altul decat la traseul de dinainte.
 *
 * "Run traceroute now" pune o comanda CQT; cat timp asteapta agentul, butonul
 * spune asta si ecranul se reintreaba des (cqPathQuery).
 */

export const REASON_LABEL: Record<CqTraceReason, string> = {
  manual: 'manual',
  periodic: 'periodic',
  anomaly: 'after an anomaly',
}

function optionLabel(t: CqTraceItem): string {
  return `${t.at} · ${REASON_LABEL[t.reason] ?? t.reason}${t.changed ? ' · path changed' : ''}${t.reached ? '' : ' · target not reached'}`
}

export function CqPath({ id }: { id: number }) {
  const [selected, setSelected] = useState<number | null>(null)
  const [runError, setRunError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { data, error } = useQuery(cqPathQuery(id, selected))

  const run = useMutation({
    mutationFn: () => runTraceroute(id),
    onSuccess: (page) => {
      setRunError(null)
      setSelected(null)
      queryClient.setQueryData(cqPathQuery(id, null).queryKey, page)
    },
    onError: (e: Error) => {
      setRunError(e.message)
      void queryClient.invalidateQueries({ queryKey: ['device', id, 'connection-quality', 'path'] })
    },
  })

  if (error) {
    return (
      <div className="cq-path">
        <h5>Path</h5>
        <div className="cq-note">The path could not be loaded: {error.message}</div>
      </div>
    )
  }
  if (!data) return <Spinner />

  const busy = data.run.state !== 'idle' || run.isPending
  const buttonText =
    data.run.state === 'pending' ? 'Waiting for the agent…' : data.run.state === 'running' ? 'Tracing…' : 'Run traceroute now'

  return (
    <div className="cq-path" data-section="path">
      <div className="cq-path-head">
        <h5>Path to {data.trace?.target ?? data.target}</h5>
        <div className="cq-path-tools">
          {data.traces.length > 0 ? (
            <select
              className="form-control input-sm"
              aria-label="Trace"
              value={selected ?? data.trace?.id ?? ''}
              onChange={(e) => setSelected(Number(e.target.value) || null)}
            >
              {data.traces.map((t) => (
                <option key={t.id} value={t.id}>
                  {optionLabel(t)}
                </option>
              ))}
            </select>
          ) : null}
          {data.canRun ? (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={busy || !data.enabled}
              title={!data.enabled ? 'Connection Quality is turned off for this device' : undefined}
              onClick={() => run.mutate()}
            >
              {busy ? <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> : <i className="fas fa-route" aria-hidden="true"></i>}{' '}
              {buttonText}
            </button>
          ) : null}
        </div>
      </div>
      {runError ? <div className="alert alert-warning cq-path-alert">{runError}</div> : null}
      {data.run.state !== 'idle' && data.run.since ? (
        <div className="cq-note">Traceroute requested at {data.run.since}. The result appears here when the agent sends it.</div>
      ) : null}

      {data.trace ? (
        <CqTraceView trace={data.trace} />
      ) : (
        <div className="cq-muted cq-path-empty">
          No traceroute yet. The agent runs one periodically and when it sees a problem
          {data.canRun ? ', or you can run one now.' : '.'}
        </div>
      )}
    </div>
  )
}

/** Un traseu desenat: semnul de drum schimbat, rezumatul, graficul, explicatia si tabelul pe hop. */
export function CqTraceView({ trace, deviceName }: { trace: CqTraceDetail; deviceName?: string }) {
  return (
    <div className="cq-trace">
      <div className="cq-trace-summary">
        {trace.changed ? (
          <span className="cq-path-changed" data-changed="yes">
            <i className="fas fa-random" aria-hidden="true"></i> Path changed
          </span>
        ) : null}
        <span>{trace.summary}</span>
        <span className="cq-muted">
          {' '}
          · {trace.at} · {REASON_LABEL[trace.reason] ?? trace.reason} · {trace.hop_count} hops
        </span>
      </div>
      <CqPathGraph trace={trace} deviceName={deviceName} />
      <p className="cq-path-explain">
        <i className="fas fa-info-circle" aria-hidden="true"></i> Loss on a hop in the middle that does <strong>not</strong> continue to
        the next hops is not a problem: routers on the way limit how often they answer ping, while the traffic itself passes through
        them. Only loss or delay that continues all the way to the target is marked as a problem (red).
      </p>
      <div className="table-responsive">
        <table className="table table-condensed cq-hops">
          <thead>
            <tr>
              <th>TTL</th>
              <th>IP</th>
              <th>Name</th>
              <th className="num">Loss</th>
              <th className="num">Min</th>
              <th className="num">Avg</th>
              <th className="num">Max</th>
              <th className="num">Jitter</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {trace.hops.map((h, i) => (
              <tr key={`${h.ttl}-${i}`} className={`is-${h.state}`} data-hop={h.ttl}>
                <td>{h.ttl}</td>
                <td>{h.ip ?? '*'}</td>
                <td>{h.host ?? ''}</td>
                <td className="num">{h.loss_pct === null ? '-' : `${h.loss_pct}%`}</td>
                <td className="num">{h.rtt_min ?? '-'}</td>
                <td className="num">{h.rtt_avg ?? '-'}</td>
                <td className="num">{h.rtt_max ?? '-'}</td>
                <td className="num">{h.jitter ?? '-'}</td>
                <td className="cq-hop-state" title={h.note}>
                  {h.state === 'bad' ? (h.origin ? 'Problem starts here' : 'Problem') : h.state === 'limited' ? 'Rate-limited (not a problem)' : h.state === 'silent' ? 'No answer' : 'OK'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
